// Dev-only bridge that lets the separate help-section-app tool preview and
// live-edit this app's translations. Fully inert unless BOTH:
//   1) NODE_ENV === "development" (never runs in a production build), and
//   2) help mode was activated at some point this tab session (see below).
// Nothing here touches any existing component, route, or business logic.
import i18n from "../i18n";

const HELP_MODE_PARAM = "helpMode";
const HELP_MODE_STORAGE_KEY = "__help_mode_active__";
const HELP_MODE_HOST_ORIGIN = process.env.REACT_APP_HELP_MODE_ORIGIN || window.location.origin;

// Auth0 login is a full top-level redirect to a different origin
// (se-prosumer.auth0.com) and back to /callback - that redirect drops any
// query params we didn't put there ourselves, so ?helpMode=1 disappears
// after login. sessionStorage survives that round-trip (same tab, same
// origin before/after), so use it to remember help mode was turned on.
function isHelpModeActive() {
  if (process.env.NODE_ENV !== "development") return false;
  try {
    if (new URLSearchParams(window.location.search).get(HELP_MODE_PARAM) === "1") {
      sessionStorage.setItem(HELP_MODE_STORAGE_KEY, "1");
      return true;
    }
    return sessionStorage.getItem(HELP_MODE_STORAGE_KEY) === "1";
  } catch {
    return false;
  }
}

function normalize(text) {
  return (text || "").replace(/\s+/g, " ").trim().toLowerCase();
}

// Works whether this app is opened as a popup window (window.opener) or
// embedded in an iframe (window.parent) - either way, send status back to
// the help-section-app tool driving this session.
function postToHost(message) {
  const target = window.opener || (window.parent !== window ? window.parent : null);
  target?.postMessage(message, "*");
}

// A hint's "tag" is only usable as a CSS type selector when it's a real DOM
// tag / custom element (lowercase, e.g. "se-button"). Capitalized tags are
// React component names and don't exist in the rendered DOM.
function isDomTag(tag) {
  return typeof tag === "string" && /^[a-z]/.test(tag) && !tag.includes(".");
}

function attrValue(el) {
  return el.getAttribute("placeholder") || el.getAttribute("aria-label") || el.getAttribute("title") || el.value;
}

// Uses the static key manifest (built by help-section-app from MGA-Frontend's
// own source) to search only the elements a key is actually known to render
// into, instead of scanning the whole document's text nodes blindly.
function findElementFromHints(norm, hints) {
  for (const hint of hints) {
    if (hint.kind === "attr" && hint.attr) {
      const selector = isDomTag(hint.tag) ? `${hint.tag}[${hint.attr}]` : `[${hint.attr}]`;
      for (const el of document.querySelectorAll(selector)) {
        if (normalize(el.getAttribute(hint.attr) ?? el.value) === norm) return el;
      }
    }
    if ((hint.kind === "children" || hint.kind === "trans") && isDomTag(hint.tag)) {
      for (const el of document.querySelectorAll(hint.tag)) {
        if (normalize(el.textContent) === norm) return el;
      }
    }
  }
  // Same hints, looser pass: substring match, for text split across nested
  // child nodes (icons, interpolation) that never render as an exact match.
  for (const hint of hints) {
    if ((hint.kind === "children" || hint.kind === "trans") && isDomTag(hint.tag)) {
      for (const el of document.querySelectorAll(hint.tag)) {
        const text = normalize(el.textContent);
        if (text && norm && text.includes(norm)) return el;
      }
    }
  }
  return null;
}

// Reverse check for design-mode picks: does any of this key's manifest
// hints actually resolve (in the live DOM) to the element that was clicked -
// as opposed to merely sharing its displayed text/tag? Dynamically generated
// content (chart labels, canvas/SVG nodes, etc.) has no manifest hint that
// resolves to it, so it correctly never matches here.
function elementMatchesHints(el, hints = []) {
  if (!el) return false;
  for (const hint of hints) {
    if (hint.kind === "attr" && hint.attr) {
      const selector = isDomTag(hint.tag) ? `${hint.tag}[${hint.attr}]` : `[${hint.attr}]`;
      for (const cand of document.querySelectorAll(selector)) {
        if (cand === el || cand.contains(el) || el.contains(cand)) return true;
      }
    }
    if ((hint.kind === "children" || hint.kind === "trans") && isDomTag(hint.tag)) {
      for (const cand of document.querySelectorAll(hint.tag)) {
        if (cand === el || cand.contains(el) || el.contains(cand)) return true;
      }
    }
  }
  return false;
}

// Finds the on-screen element whose text (or placeholder/aria-label/title)
// matches the given translation value, so it can be highlighted exactly -
// no OCR/screenshot approximation needed since this is the real live DOM.
function findElementForText(value, hints = []) {
  const norm = normalize(value);
  if (!norm) return null;

  const hinted = hints.length ? findElementFromHints(norm, hints) : null;
  if (hinted) return hinted;

  const walker = document.createTreeWalker(document.body, NodeFilter.SHOW_TEXT);
  let node = walker.nextNode();
  while (node) {
    if (normalize(node.textContent) === norm && node.parentElement) {
      return node.parentElement;
    }
    node = walker.nextNode();
  }

  const candidates = document.querySelectorAll("[placeholder], [aria-label], [title], input, textarea");
  for (const el of candidates) {
    if (normalize(attrValue(el)) === norm) return el;
  }
  return null;
}

let overlayEl = null;
let lastTarget = null;
// The exact element clicked in design mode, held only until help-section-app
// verifies which (if any) candidate key's hints actually resolve to it.
let pendingPickEl = null;

// Draws the highlight directly in this window's own document (position:fixed
// means viewport-relative coordinates, no cross-window math needed at all).
function ensureOverlay() {
  if (overlayEl && document.body.contains(overlayEl)) return overlayEl;
  overlayEl = document.createElement("div");
  overlayEl.id = "__help_mode_highlight";
  Object.assign(overlayEl.style, {
    position: "fixed",
    zIndex: "2147483647",
    pointerEvents: "none",
    border: "3px solid #dc2626",
    background: "rgba(220, 38, 38, 0.15)",
    borderRadius: "3px",
    boxShadow: "0 0 0 3px rgba(220, 38, 38, 0.25)",
    transition: "top 0.15s ease, left 0.15s ease, width 0.15s ease, height 0.15s ease",
    display: "none",
  });
  document.body.appendChild(overlayEl);
  return overlayEl;
}

function positionOverlay(el) {
  const rect = el.getBoundingClientRect();
  const box = ensureOverlay();
  Object.assign(box.style, {
    display: "block",
    top: `${rect.top}px`,
    left: `${rect.left}px`,
    width: `${rect.width}px`,
    height: `${rect.height}px`,
  });
}

function clearOverlay() {
  lastTarget = null;
  if (overlayEl) overlayEl.style.display = "none";
}

function highlight(key, value, hints) {
  const el = findElementForText(value, hints);
  lastTarget = el;
  if (el) {
    el.scrollIntoView({ block: "center", behavior: "smooth" });
    positionOverlay(el);
  } else {
    clearOverlay();
  }
  postToHost({ type: "HELP_HIGHLIGHT_RESULT", key, found: !!el });
}

// "Design mode": instead of interacting with the app normally, clicks are
// captured and the clicked element's visible text is reported back so
// help-section-app can find and select the matching translation key -
// the reverse of the normal "click a key, see it highlighted here" flow.
function elementOwnText(el) {
  const attr = attrValue(el);
  if (attr) return attr.trim();
  return (el.innerText || el.textContent || "").trim();
}

function findLabeledAncestor(el) {
  let cur = el;
  let hops = 0;
  while (cur && cur !== document.body && hops < 6) {
    const text = elementOwnText(cur);
    if (text) return { el: cur, text };
    cur = cur.parentElement;
    hops++;
  }
  return null;
}

function handleDesignClick(event) {
  event.preventDefault();
  event.stopPropagation();
  const found = findLabeledAncestor(event.target);
  if (!found) return;
  // Highlight the exact clicked element immediately (we already have the
  // real reference) rather than waiting on a value-based re-search, which
  // can land on the wrong element when the same text appears more than
  // once (e.g. "Projects" as both a button and a link).
  lastTarget = found.el;
  pendingPickEl = found.el;
  positionOverlay(found.el);
  postToHost({
    type: "HELP_ELEMENT_PICKED",
    text: found.text,
    tag: found.el.tagName.toLowerCase(),
    id: found.el.id || null,
    dataTestId: found.el.getAttribute("data-testid") || null,
  });
}

function setDesignMode(active) {
  document.removeEventListener("click", handleDesignClick, true);
  if (active) {
    document.addEventListener("click", handleDesignClick, true);
    document.body.style.cursor = "crosshair";
  } else {
    document.body.style.cursor = "";
  }
}

function handleMessage(event) {
  const target = window.opener || (window.parent !== window ? window.parent : null);
  if (event.origin !== HELP_MODE_HOST_ORIGIN || (target && event.source !== target)) return;

  const data = event.data;
  if (!data || typeof data !== "object") return;

  if (data.type === "HELP_SET_VALUE") {
    i18n.addResource(data.lang, "translation", data.key, data.value);
    if (i18n.language === data.lang) i18n.emit("languageChanged", data.lang);
    return;
  }

  if (data.type === "HELP_SET_LANG") {
    i18n.changeLanguage(data.lang);
    return;
  }

  if (data.type === "HELP_SET_DESIGN_MODE") {
    setDesignMode(!!data.active);
    return;
  }

  if (data.type === "HELP_HIGHLIGHT") {
    highlight(data.key, data.value, data.hints);
    return;
  }

  if (data.type === "HELP_VERIFY_PICK") {
    const el = pendingPickEl;
    pendingPickEl = null;
    const match = (data.candidates || []).find((c) => elementMatchesHints(el, c.hints));
    postToHost({ type: "HELP_VERIFY_PICK_RESULT", key: match ? match.key : null });
  }
}

export function initHelpMode() {
  if (!isHelpModeActive()) return;
  window.addEventListener("message", handleMessage);
  window.addEventListener(
    "scroll",
    () => {
      if (lastTarget && document.body.contains(lastTarget)) positionOverlay(lastTarget);
    },
    true
  );
  postToHost({ type: "HELP_MODE_READY" });
}
